export const SVG_MIME_TYPE = 'image/svg+xml'

/**
 * Elements that are never needed to draw static artwork and are the usual SVG
 * script-execution or embedded-content vectors. SMIL animation is in the list
 * because `<set attributeName="onload" .../>` is a known handler-injection
 * trick and animation is irrelevant to a static drawing anyway.
 */
const DISALLOWED_SVG_ELEMENTS = new Set([
  'script',
  'foreignobject',
  'iframe',
  'embed',
  'object',
  'link',
  'base',
  'meta',
  'handler',
  'listener',
  'animate',
  'animatemotion',
  'animatetransform',
  'set',
  'audio',
  'video',
])

/** Attributes carrying a URL, matched on the local name so a prefix like
 * `xlink:` (or any other binding of it) is covered. */
const URL_ATTRIBUTE_NAMES = new Set(['href', 'src'])

/**
 * `url(...)` targets inside any attribute value, e.g. `fill="url(#grad)"`.
 * The quoted forms are matched separately from the bare one so that a target
 * containing parentheses — `url("javascript:alert(1)")` — is still captured.
 */
const URL_FUNCTION_PATTERN = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)]*))\s*\)/gi

/**
 * Same-document fragments and embedded rasters only. `data:image/svg+xml` is
 * deliberately excluded: a nested SVG would carry its own unsanitized payload.
 */
const SAFE_URL_VALUE_PATTERN = /^(?:#|data:image\/(?:png|jpe?g|gif|webp);)/

/**
 * Whether a URL an SVG points at is safe to keep. Rejects `javascript:` (script
 * execution) and remote references (which would make the SVG fetch third-party
 * content whenever it is viewed).
 */
export function isSafeSvgUrlValue(value: string): boolean {
  // Whitespace is stripped before the scheme test because `java\tscript:`
  // and ` javascript:` are still honoured by browsers. \s is enough here: the
  // other C0 control characters are rejected by the XML parse this value comes
  // from, and entity-encoded ones are already decoded by it.
  const normalized = value.replace(/\s+/g, '').toLowerCase()
  return normalized === '' || SAFE_URL_VALUE_PATTERN.test(normalized)
}

/** Whether an element is one of the disallowed SVG elements. */
export function isDisallowedSvgElement(localName: string): boolean {
  return DISALLOWED_SVG_ELEMENTS.has(localName.toLowerCase())
}

/**
 * Whether an attribute is safe to keep on a sanitized SVG: no event handlers,
 * and no unsafe URL, whether it is the whole value or a `url(...)` inside it.
 */
export function isSafeSvgAttribute(name: string, value: string): boolean {
  const localName = name.toLowerCase().split(':').pop() ?? ''
  if (localName.startsWith('on')) {
    return false
  }
  if (URL_ATTRIBUTE_NAMES.has(localName) && !isSafeSvgUrlValue(value)) {
    return false
  }

  const targets = [...value.matchAll(URL_FUNCTION_PATTERN)].map(
    ([, doubleQuoted, singleQuoted, bare]) =>
      doubleQuoted ?? singleQuoted ?? bare ?? '',
  )
  // Fail closed: a `url(` the pattern could not read is dropped rather than
  // waved through, so a parsing gap here cannot become a way in.
  if (targets.length === 0) {
    return !/url\(/i.test(value)
  }
  return targets.every((target) => isSafeSvgUrlValue(target))
}

/**
 * Parses `file` as SVG, rejecting anything that is not a real SVG document.
 * Browser-only (DOMParser).
 */
export async function parseSvgRoot(file: File): Promise<Element> {
  const parsed = new DOMParser().parseFromString(
    await file.text(),
    SVG_MIME_TYPE,
  )
  const svg = parsed.documentElement
  if (svg.nodeName !== 'svg' || parsed.querySelector('parsererror')) {
    throw new Error('The file is not a parseable SVG')
  }
  return svg
}

/** Serializes `svg` back into a File keeping `file`'s name and mime type. */
export function serializeSvgToFile(svg: Element, file: File): File {
  return new File([new XMLSerializer().serializeToString(svg)], file.name, {
    type: file.type,
  })
}

/**
 * Strips script-execution and external-reference vectors from an SVG file:
 * disallowed elements, event-handler attributes, and unsafe URL references.
 *
 * Worth doing for any SVG that will be served from a URL someone can open
 * directly. Opening such a URL renders the SVG as a document rather than as an
 * image, so a `<script>` or an `onload=` inside it runs on the serving origin.
 * Static artwork never needs script, animation or remote refs, so all three are
 * removed rather than the file being rejected — real exports only trip over
 * this with content the drawing does not need.
 *
 * Browser-only (DOMParser), so callers that need to cover this traversal should
 * inject it; the policy it applies is unit-tested through isDisallowedSvgElement
 * and isSafeSvgAttribute.
 */
export async function sanitizeSvgFile(file: File): Promise<File> {
  const svg = await parseSvgRoot(file)

  for (const element of Array.from(svg.querySelectorAll('*'))) {
    if (isDisallowedSvgElement(element.localName)) {
      element.remove()
    }
  }

  // After the removals so the stripped subtrees are not walked at all.
  for (const element of [svg, ...Array.from(svg.querySelectorAll('*'))]) {
    for (const attribute of Array.from(element.attributes)) {
      if (!isSafeSvgAttribute(attribute.name, attribute.value)) {
        element.removeAttribute(attribute.name)
      }
    }
  }

  return serializeSvgToFile(svg, file)
}
